import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import Button from "../../componentes/button/button";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import RenovacaoGdmService from "../../services/resource/renovacaoGdmService";
import { formatarDataHora } from "../../utils/solicitacoes/formatters";

const service = new RenovacaoGdmService();

const COLUNAS_PLANILHA = [
    ["C", "Rota (guarda só o que vem após o \"-\")"], ["D", "GDM Status"], ["F", "Status PDV"],
    ["H", "Código do Cliente (6648-0000-0000)"], ["I", "Canal"], ["J", "Razão Social"], ["K", "Nome Fantasia"],
    ["M", "Cidade"], ["N", "Data de Aquisição"], ["O", "GDM Ativo"], ["P", "Número de Série"],
    ["Q", "Contrato (CEV)"], ["R", "GDM Código"], ["S", "GDM Descrição"], ["T", "Logomarca"],
    ["U", "Modelo de Fabricação"], ["Z", "Código Produto SAP"],
];

const FILTROS_VAZIOS = { termo: "", rota: null, cidade: null, canal: null, gdmStatus: null, logomarca: null, origem: "" };
const OPCOES_VAZIAS = { rotas: [], cidades: [], canais: [], gdmStatus: [], logomarcas: [] };
const ORIGENS = [{ value: "", label: "Todas" }, { value: "PLANILHA", label: "Planilha" }, { value: "MANUAL", label: "Manual" }];

const FORM_VAZIO = {
    id: null, clienteCodigo: "", razaoSocial: "", nomeFantasia: "", rota: "", gdmStatus: "", statusPdv: "", canal: "",
    cidade: "", dataAquisicao: "", gdmAtivo: "", numeroSerie: "", numeroContratoCev: "", gdmCodigo: "", gdmDescricao: "",
    logomarca: "", modeloFabricacao: "", codigoProdutoSap: "",
};

/** Campos do formulário: [campo, rótulo, lista de sugestões (chave de `opcoes`), obrigatório]. */
const CAMPOS_FORM = [
    ["numeroSerie", "Nº de Série"], ["gdmAtivo", "GDM Ativo"], ["rota", "Rota", "rotas"],
    ["gdmStatus", "GDM Status", "gdmStatus"], ["statusPdv", "Status PDV"], ["canal", "Canal", "canais"],
    ["cidade", "Cidade", "cidades"], ["numeroContratoCev", "Contrato CEV"], ["gdmCodigo", "GDM Código"],
    ["gdmDescricao", "GDM Descrição"], ["logomarca", "Logomarca", "logomarcas"], ["modeloFabricacao", "Modelo de Fabricação"],
    ["codigoProdutoSap", "Código Produto SAP"],
];

/** "00010001" (base) → "0001-0001". */
const formatarCodigo = c => (c && c.length === 8 ? `${c.slice(0, 4)}-${c.slice(4)}` : c || "");

const mensagemErro = (e, padrao) => e?.response?.data?.message || padrao;

const paraLinha = r => ({
    id: r.id, clienteCodigo: formatarCodigo(r.clienteCodigo), razaoSocial: r.razaoSocial, nomeFantasia: r.nomeFantasia,
    ...(r.gdm || {}), origem: r.origem, dataAlteracao: r.dataAlteracao, usuarioAlteracao: r.usuarioAlteracao,
});

function Indicador({ label, valor, icone }) {
    return (
        <div className="bg-card border border-border rounded-lg shadow-card p-4 flex items-center gap-3">
            <i className={`${icone} text-primary text-xl w-8 text-center`} />
            <div>
                <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</span>
                <span className="text-lg font-semibold text-foreground">{valor}</span>
            </div>
        </div>
    );
}

function ListaMensagens({ titulo, total, mensagens, cor }) {
    if (!total) return null;
    return (
        <div>
            <h4 className={`text-xs font-semibold uppercase tracking-wide mb-1 ${cor}`}>
                {titulo} ({total}{mensagens.length < total ? ` — exibindo ${mensagens.length}` : ""})
            </h4>
            <ul className="text-xs text-foreground max-h-48 overflow-y-auto border border-border rounded-md p-2 space-y-0.5">
                {mensagens.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
        </div>
    );
}

function ResultadoImportacao({ resultado, onFechar }) {
    const numeros = [
        ["Linhas lidas", resultado.linhasLidas, ""],
        ["Importados", resultado.importados, "text-green-700"],
        ["Ignorados (erro)", resultado.ignorados, "text-destructive"],
        ["Duplicados", resultado.duplicados, ""],
        ["Substituídos da base anterior", resultado.removidosBaseAnterior, ""],
    ];
    return (
        <div className="bg-card border border-border rounded-lg shadow-card p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-base font-semibold text-foreground">Resultado da importação</h2>
                <button type="button" onClick={onFechar} className="text-muted-foreground hover:text-foreground" title="Fechar">
                    <i className="fa fa-times" />
                </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                {numeros.map(([label, valor, cor]) => (
                    <div key={label}>
                        <span className="block text-[11px] uppercase text-muted-foreground font-semibold">{label}</span>
                        <span className={`font-semibold ${cor}`}>{valor}</span>
                    </div>
                ))}
            </div>
            <ListaMensagens titulo="Erros (linhas descartadas)" total={resultado.totalErros} mensagens={resultado.erros} cor="text-destructive" />
            <ListaMensagens titulo="Avisos" total={resultado.totalAvisos} mensagens={resultado.avisos} cor="text-amber-700" />
        </div>
    );
}

export default function BaseGdm() {
    const navigate = useNavigate();
    const [resumo, setResumo] = useState(null);
    const [opcoes, setOpcoes] = useState(OPCOES_VAZIAS);

    const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
    const [listagem, setListagem] = useState({ total: 0, limite: 0, registros: [] });
    const [carregando, setCarregando] = useState(false);
    const [selecionados, setSelecionados] = useState([]);

    const [form, setForm] = useState(FORM_VAZIO);
    const [modalAberto, setModalAberto] = useState(false);
    const [salvando, setSalvando] = useState(false);

    const [importacaoAberta, setImportacaoAberta] = useState(false);
    const [arquivo, setArquivo] = useState(null);
    const [importando, setImportando] = useState(false);
    const [resultado, setResultado] = useState(null);

    const podeGerenciar = !!resumo?.podeGerenciar;

    const carregarResumo = useCallback(() => {
        service.resumoBaseGdm().then(res => setResumo(res.data)).catch(() => setResumo(null));
        service.opcoesBaseGdm().then(res => setOpcoes({ ...OPCOES_VAZIAS, ...res.data })).catch(() => setOpcoes(OPCOES_VAZIAS));
    }, []);

    const pesquisar = useCallback((filtrosBusca) => {
        setCarregando(true);
        setSelecionados([]);
        service.listarBaseGdm({ ...filtrosBusca, termo: filtrosBusca.termo.trim() })
            .then(res => setListagem({ ...res.data, registros: (res.data.registros || []).map(paraLinha) }))
            .catch(e => toast.error(mensagemErro(e, "Erro ao carregar a base de GDM.")))
            .finally(() => setCarregando(false));
    }, []);

    useEffect(() => {
        document.title = "MyDash | Base de GDM";
        carregarResumo();
        pesquisar(FILTROS_VAZIOS);
    }, [carregarResumo, pesquisar]);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    function limparFiltros() {
        setFiltros(FILTROS_VAZIOS);
        pesquisar(FILTROS_VAZIOS);
    }

    function recarregar() {
        carregarResumo();
        pesquisar(filtros);
    }

    // ------------------------------------------------------------ cadastro / edição

    function abrirModal(linha) {
        setForm(linha ? Object.fromEntries(Object.keys(FORM_VAZIO).map(k => [k, linha[k] ?? (k === "id" ? null : "")])) : FORM_VAZIO);
        setModalAberto(true);
    }

    function salvar() {
        if (!form.clienteCodigo.trim()) {
            toast.error("Informe o código do cliente.");
            return;
        }
        if (!form.numeroSerie.trim() && !form.gdmAtivo.trim()) {
            toast.error("Informe o nº de série ou o GDM Ativo.");
            return;
        }
        const { id, ...dados } = form;
        const payload = { ...dados, dataAquisicao: dados.dataAquisicao || null };
        setSalvando(true);
        (id ? service.atualizarBaseGdm(id, payload) : service.criarBaseGdm(payload))
            .then(() => {
                toast.success(id ? "Registro atualizado." : "Registro cadastrado.");
                setModalAberto(false);
                recarregar();
            })
            .catch(e => toast.error(mensagemErro(e, "Não foi possível salvar o registro.")))
            .finally(() => setSalvando(false));
    }

    function excluir(linha) {
        if (!window.confirm(`Excluir da base a GDM ${linha.numeroSerie || linha.gdmAtivo} do cliente ${linha.clienteCodigo}?`)) return;
        service.excluirBaseGdm(linha.id)
            .then(() => {
                toast.success("Registro excluído.");
                recarregar();
            })
            .catch(e => toast.error(mensagemErro(e, "Não foi possível excluir o registro.")));
    }

    function excluirSelecionados() {
        if (!window.confirm(`Excluir ${selecionados.length} registro(s) da base de GDM?`)) return;
        service.excluirBaseGdmEmLote(selecionados)
            .then(res => {
                toast.success(`${res.data.excluidos} registro(s) excluído(s).`);
                recarregar();
            })
            .catch(e => toast.error(mensagemErro(e, "Não foi possível excluir os registros.")));
    }

    // ------------------------------------------------------------ importação

    async function importar() {
        if (!arquivo) {
            toast.warning("Selecione a planilha da base de GDM.");
            return;
        }
        const formData = new FormData();
        formData.append("file", arquivo);
        setImportando(true);
        try {
            const res = await service.importarBaseGdm(formData);
            setResultado(res.data);
            toast.success(`Base de GDM importada: ${res.data.importados} registros.`);
            setImportacaoAberta(false);
            setArquivo(null);
            recarregar();
        } catch (e) {
            toast.error(mensagemErro(e, "Erro ao importar a planilha."));
        } finally {
            setImportando(false);
        }
    }

    const colunas = [
        { key: "clienteCodigo", label: "Código Cliente" },
        { key: "razaoSocial", label: "Razão Social" },
        { key: "nomeFantasia", label: "Nome Fantasia" },
        { key: "rota", label: "Rota" },
        { key: "gdmStatus", label: "GDM Status" },
        { key: "statusPdv", label: "Status PDV" },
        { key: "canal", label: "Canal" },
        { key: "cidade", label: "Cidade" },
        { key: "dataAquisicao", label: "Data Aquisição", format: "date" },
        { key: "gdmAtivo", label: "GDM Ativo" },
        { key: "numeroSerie", label: "Nº de Série" },
        { key: "numeroContratoCev", label: "Contrato CEV" },
        { key: "gdmCodigo", label: "GDM Código" },
        { key: "gdmDescricao", label: "GDM Descrição" },
        { key: "logomarca", label: "Logomarca" },
        { key: "modeloFabricacao", label: "Modelo Fabricação" },
        { key: "codigoProdutoSap", label: "Código SAP" },
        { key: "origem", label: "Origem", render: v => (v === "MANUAL" ? "Manual" : "Planilha") },
        {
            key: "dataAlteracao", label: "Alterado em",
            render: (v, linha) => (v ? `${formatarDataHora(v)}${linha.usuarioAlteracao ? ` — ${linha.usuarioAlteracao}` : ""}` : "-"),
        },
        ...(podeGerenciar ? [{
            key: "acoes", label: "Ações", center: true, render: (_, linha) => (
                <div className="flex justify-center gap-2">
                    <button type="button" title="Editar" onClick={e => { e.stopPropagation(); abrirModal(linha); }}
                        className="text-primary hover:bg-primary/10 rounded-full p-1">
                        <i className="fa fa-pencil" />
                    </button>
                    <button type="button" title="Excluir" onClick={e => { e.stopPropagation(); excluir(linha); }}
                        className="text-destructive hover:bg-destructive/10 rounded-full p-1">
                        <i className="fa fa-trash" />
                    </button>
                </div>
            ),
        }] : []),
    ];

    const filtroDropdown = (campo, label, lista) => (
        <Campo label={label}>
            <CustomDropdown value={filtros[campo]} onChange={e => setFiltro(campo, e.value)}
                options={(opcoes[lista] || []).map(v => ({ value: v, label: v }))} placeholder="Todos" showClear filter />
        </Campo>
    );

    return (
        <div className="flex flex-col gap-4 p-4 md:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="font-display text-xl font-semibold text-foreground">Base de GDM</h1>
                    <p className="text-sm text-muted-foreground">Base usada na Renovação de GDM para identificar as geladeiras de cada cliente.</p>
                </div>
                {podeGerenciar && (
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" label="Relatório de conclusão" iconLeft="fa fa-chart-pie"
                            onClick={() => navigate("/solicitacoes/relatorios/conclusao-gdm")} />
                        <Button variant="outline" label="Importar planilha" iconLeft="fa fa-file-excel" onClick={() => setImportacaoAberta(true)} />
                        <Button variant="blue" label="Novo registro" iconLeft="fa fa-plus" onClick={() => abrirModal(null)} />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Indicador label="Registros na base" valor={resumo ? resumo.totalRegistros.toLocaleString("pt-BR") : "-"} icone="fa fa-database" />
                <Indicador label="Última importação" valor={resumo?.dataUltimaImportacao ? formatarDataHora(resumo.dataUltimaImportacao) : "-"} icone="fa fa-clock" />
                <Indicador label="Importado por" valor={resumo?.usuarioUltimaImportacao || "-"} icone="fa fa-user" />
            </div>

            {resultado && <ResultadoImportacao resultado={resultado} onFechar={() => setResultado(null)} />}

            <div className="bg-card border border-border rounded-lg shadow-card p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-foreground">Filtros</h2>
                    <button type="button" onClick={limparFiltros} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
                        Limpar filtros
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="sm:col-span-2">
                        <Campo label="Pesquisar">
                            <input type="text" value={filtros.termo} onChange={e => setFiltro("termo", e.target.value)}
                                onKeyDown={e => e.key === "Enter" && pesquisar(filtros)}
                                placeholder="Código ou nome do cliente, nº de série, GDM ativo ou contrato CEV" className={INPUT_CLASS} />
                        </Campo>
                    </div>
                    {filtroDropdown("rota", "Rota", "rotas")}
                    {filtroDropdown("cidade", "Cidade", "cidades")}
                    {filtroDropdown("canal", "Canal", "canais")}
                    {filtroDropdown("gdmStatus", "GDM Status", "gdmStatus")}
                    {filtroDropdown("logomarca", "Logomarca", "logomarcas")}
                    <Campo label="Origem">
                        <div className="flex gap-1 rounded-lg border border-border p-1 bg-card h-9 items-center">
                            {ORIGENS.map(op => (
                                <button key={op.value} type="button" onClick={() => setFiltro("origem", op.value)}
                                    className={`flex-1 px-2 py-1 rounded-md text-xs font-medium ${filtros.origem === op.value ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`}>
                                    {op.label}
                                </button>
                            ))}
                        </div>
                    </Campo>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                        {listagem.total > listagem.registros.length
                            ? `Exibindo ${listagem.registros.length.toLocaleString("pt-BR")} de ${listagem.total.toLocaleString("pt-BR")} registros — refine os filtros para ver os demais.`
                            : `${listagem.total.toLocaleString("pt-BR")} registro(s).`}
                    </span>
                    <div className="flex gap-2">
                        {podeGerenciar && selecionados.length > 0 && (
                            <Button variant="outline" label={`Excluir selecionados (${selecionados.length})`} iconLeft="fa fa-trash"
                                onClick={excluirSelecionados} />
                        )}
                        <Button variant="blue" label="Pesquisar" iconLeft="fa fa-search" loading={carregando} onClick={() => pesquisar(filtros)} />
                    </div>
                </div>
            </div>

            <CustomTable
                data={listagem.registros}
                columns={colunas}
                loading={carregando}
                pagination
                pageSize={20}
                exportable
                showHeader={false}
                className="text-[11px] border shadow-card rounded-xl"
                exportFilename="base_gdm"
                footer={() => null}
                selectable={podeGerenciar}
                rowKey="id"
                selectedKeys={selecionados}
                onSelectionChange={setSelecionados}
            />

            <Dialog
                header={form.id ? "Editar registro da base de GDM" : "Novo registro na base de GDM"}
                visible={modalAberto}
                onHide={() => setModalAberto(false)}
                blockScroll
                className="w-[96vw] max-w-4xl"
                footer={(
                    <div className="flex justify-end gap-2 pt-3">
                        <Button variant="outline" label="Cancelar" onClick={() => setModalAberto(false)} />
                        <Button variant="blue" label="Salvar" iconLeft="fa fa-check" loading={salvando} disabled={salvando} onClick={salvar} />
                    </div>
                )}
            >
                <div className="flex flex-col gap-4 pt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <Campo label="Código do Cliente" required>
                            <input type="text" value={form.clienteCodigo} placeholder="0000-0000"
                                onChange={e => setForm(f => ({ ...f, clienteCodigo: e.target.value }))} className={INPUT_CLASS} />
                        </Campo>
                        <div className="lg:col-span-2">
                            <Campo label="Razão Social">
                                <input type="text" value={form.razaoSocial} onChange={e => setForm(f => ({ ...f, razaoSocial: e.target.value }))} className={INPUT_CLASS} />
                            </Campo>
                        </div>
                        <Campo label="Nome Fantasia">
                            <input type="text" value={form.nomeFantasia} onChange={e => setForm(f => ({ ...f, nomeFantasia: e.target.value }))} className={INPUT_CLASS} />
                        </Campo>
                        {CAMPOS_FORM.map(([campo, label, lista]) => (
                            <Campo key={campo} label={label}>
                                <input type="text" value={form[campo]} list={lista ? `base-gdm-${lista}` : undefined}
                                    onChange={e => setForm(f => ({ ...f, [campo]: e.target.value }))} className={INPUT_CLASS} />
                            </Campo>
                        ))}
                        <Campo label="Data de Aquisição">
                            <input type="date" value={form.dataAquisicao} onChange={e => setForm(f => ({ ...f, dataAquisicao: e.target.value }))} className={INPUT_CLASS} />
                        </Campo>
                    </div>
                    {Object.entries(opcoes).map(([lista, valores]) => (
                        <datalist key={lista} id={`base-gdm-${lista}`}>
                            {valores.map(v => <option key={v} value={v} />)}
                        </datalist>
                    ))}
                    <p className="text-xs text-muted-foreground">
                        Informe o nº de série ou o GDM Ativo — é por eles que a renovação é ligada à GDM. Atenção: uma nova importação da
                        planilha substitui a base inteira, inclusive os ajustes feitos aqui.
                    </p>
                </div>
            </Dialog>

            <Dialog
                header="Importar planilha da base de GDM"
                visible={importacaoAberta}
                onHide={() => setImportacaoAberta(false)}
                blockScroll
                className="w-[96vw] max-w-3xl"
                footer={(
                    <div className="flex justify-end gap-2 pt-3">
                        <Button variant="outline" label="Cancelar" onClick={() => setImportacaoAberta(false)} />
                        <Button variant="blue" label={importando ? "Importando..." : "Importar"} iconLeft="fa fa-upload" loading={importando}
                            disabled={importando || !arquivo} onClick={importar} />
                    </div>
                )}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex flex-col gap-3">
                        <p className="text-muted-foreground">
                            A importação <strong>substitui a base inteira</strong> pela planilha enviada (ela é a foto atual do parque de GDM),
                            inclusive registros cadastrados ou editados manualmente. Linhas inválidas são descartadas e GDMs repetidas
                            (mesmo nº de série) são importadas uma única vez. Se nenhuma linha for válida, a base atual é mantida.
                        </p>
                        <input type="file" accept=".xlsx,.xls,.xlsm" onChange={e => setArquivo(e.target.files?.[0] || null)}
                            className="border border-input rounded-md w-full px-3 py-2 text-sm" />
                    </div>
                    <div className="text-xs text-muted-foreground">
                        <span className="font-semibold uppercase tracking-wide">Colunas lidas</span>
                        <ul className="mt-1 space-y-0.5">
                            {COLUNAS_PLANILHA.map(([col, nome]) => <li key={col}><strong>{col}</strong> — {nome}</li>)}
                        </ul>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
